import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, LinearProgress, Typography } from "@mui/material";
import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import { Evaluation, TableColumn } from "../../types";
import { formatRelativeTime, formatDateTime } from "../../utils/formatters";

const EvaluationList: React.FC = () => {
  const navigate = useNavigate();
  const alert = useAlert();

  // State variables
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [abortDialogOpen, setAbortDialogOpen] = useState<boolean>(false);
  const [selectedEvaluation, setSelectedEvaluation] =
    useState<Evaluation | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Fetch evaluations
  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        setLoading(true);
        const response = await api.evaluations.getAll();
        setEvaluations(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching evaluations:", error);
        setError("Failed to load evaluations. Please try again.");
        alert.error("Failed to fetch evaluations");
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, [alert]);

  // Helper function to get status chip
  const getStatusChip = (status: string) => {
    let color: "success" | "primary" | "warning" | "error" | "default" =
      "default";
    let label = status;

    switch (status) {
      case "completed":
        color = "success";
        label = "Completed";
        break;
      case "pending":
        color = "default";
        label = "Pending";
        break;
      case "running":
        color = "primary";
        label = "Running";
        break;
      case "failed":
        color = "error";
        label = "Failed";
        break;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  // Helper function to get progress bar
  const getProgressBar = (evaluation: Evaluation) => {
    if (evaluation.status !== "running") return null;

    // Calculate progress - this is just an example
    // In a real app, the progress should come from the API
    const progress = 50; // Default to 50% for demo

    return (
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography
            variant="body2"
            color="text.secondary"
          >{`${Math.round(progress)}%`}</Typography>
        </Box>
      </Box>
    );
  };

  // Table columns
  const columns: TableColumn[] = [
    {
      field: "_id",
      headerName: "ID",
      width: 80,
      type: "string",
    },
    {
      field: "status",
      headerName: "Status",
      type: "custom",
      render: (row: Evaluation) => getStatusChip(row.status),
    },
    {
      field: "promptIds",
      headerName: "Prompts",
      type: "custom",
      render: (row: Evaluation) => (
        <Chip
          label={row.promptIds?.length || 0}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "testIds",
      headerName: "Tests",
      type: "custom",
      render: (row: Evaluation) => (
        <Chip
          label={row.testIds?.length || 0}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "providerIds",
      headerName: "Providers",
      type: "custom",
      render: (row: Evaluation) => (
        <Chip
          label={row.providerIds?.length || 0}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "progress",
      headerName: "Progress",
      type: "custom",
      render: (row: Evaluation) => getProgressBar(row),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      type: "custom",
      render: (row: Evaluation) => formatRelativeTime(new Date(row.createdAt)),
    },
  ];

  // Handle view
  const handleView = (evaluation: Evaluation) => {
    navigate(`/evaluations/${evaluation._id}`);
  };

  // Handle delete
  const handleDelete = (evaluation: Evaluation) => {
    setSelectedEvaluation(evaluation);
    setDeleteDialogOpen(true);
  };

  // Handle abort
  const handleAbort = (evaluation: Evaluation) => {
    if (evaluation.status !== "running" && evaluation.status !== "pending") {
      alert.info("Only running or pending evaluations can be aborted");
      return;
    }

    setSelectedEvaluation(evaluation);
    setAbortDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!selectedEvaluation) return;

    try {
      setIsProcessing(true);
      await api.evaluations.delete(selectedEvaluation._id);
      setEvaluations(
        evaluations.filter((e) => e._id !== selectedEvaluation._id),
      );
      alert.success("Evaluation deleted successfully");
    } catch (error) {
      console.error("Error deleting evaluation:", error);
      alert.error("Failed to delete evaluation");
    } finally {
      setIsProcessing(false);
      setDeleteDialogOpen(false);
      setSelectedEvaluation(null);
    }
  };

  // Confirm abort
  const confirmAbort = async () => {
    if (!selectedEvaluation) return;

    try {
      setIsProcessing(true);
      await api.evaluations.abort(selectedEvaluation._id);

      // Update the evaluation status in the list
      const updatedEvaluations = evaluations.map((e) =>
        e._id === selectedEvaluation._id ? { ...e, status: "failed" } : e,
      );
      setEvaluations(updatedEvaluations as Evaluation[]);

      alert.success("Evaluation aborted successfully");
    } catch (error) {
      console.error("Error aborting evaluation:", error);
      alert.error("Failed to abort evaluation");
    } finally {
      setIsProcessing(false);
      setAbortDialogOpen(false);
      setSelectedEvaluation(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Evaluations"
        subtitle="Run and manage evaluations of your prompts"
        actionPath="/evaluations/new"
        actionLabel="New Evaluation"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Evaluations", path: "/evaluations" },
        ]}
      />

      <LoadingBackdrop open={loading && !error && evaluations.length === 0} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      <DataTable
        title="All Evaluations"
        data={evaluations}
        columns={columns}
        loading={loading}
        onView={handleView}
        onDelete={handleDelete}
        onRowClick={handleView}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Evaluation"
        message={`Are you sure you want to delete this evaluation? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        loading={isProcessing}
      />

      <ConfirmDialog
        open={abortDialogOpen}
        title="Abort Evaluation"
        message={`Are you sure you want to abort this evaluation? This will stop the evaluation process.`}
        confirmButtonText="Abort"
        confirmButtonColor="warning"
        onConfirm={confirmAbort}
        onCancel={() => setAbortDialogOpen(false)}
        loading={isProcessing}
      />
    </Box>
  );
};

export default EvaluationList;
